<lib-sales-insights-card
  [cardTitle]="config?.title"
  [subtitle]="subtitle"
  [loading]="false"
  [error]="error"
  [empty]="false"
>
  @if (canBook) {
    <lib-sales-insights-book-activity
      cardActions
      labelKey="SALES_INSIGHTS_NEW_ACTIVITY"
      icon="fa-solid fa-plus"
      [date]="bookDate"
    ></lib-sales-insights-book-activity>
  }
  <div cardToolbar class="si-calendar__toolbar">
    <div class="si-calendar__nav">
      <button
        type="button"
        class="btn btn-sm si-calendar__button si-calendar__icon"
        [attr.aria-label]="'SALES_INSIGHTS_CALENDAR_PREVIOUS' | translate"
        (click)="step(-1)"
      >
        <i class="fa-solid fa-chevron-left"></i>
      </button>
      <button
        type="button"
        class="btn btn-sm si-calendar__button si-calendar__icon"
        [attr.aria-label]="'SALES_INSIGHTS_CALENDAR_NEXT' | translate"
        (click)="step(1)"
      >
        <i class="fa-solid fa-chevron-right"></i>
      </button>
      <h4 class="si-calendar__heading">{{ heading }}</h4>
      <button type="button" class="btn btn-sm si-calendar__button" (click)="goToday()">
        {{ 'SALES_INSIGHTS_CALENDAR_TODAY' | translate }}
      </button>
      @if (loading || refreshing) {
        <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
      }
    </div>
    @if (moveError) {
      <span class="si-calendar__error"
        ><i class="fa-solid fa-triangle-exclamation me-1"></i>{{ moveError | translate }}</span
      >
    }
    <div class="si-calendar__views" role="group">
      <button
        type="button"
        class="btn btn-sm"
        [class.si-calendar__view--on]="view === 'week'"
        [attr.aria-pressed]="view === 'week'"
        (click)="setView('week')"
      >
        {{ 'SALES_INSIGHTS_CALENDAR_WEEK' | translate }}
      </button>
      <button
        type="button"
        class="btn btn-sm"
        [class.si-calendar__view--on]="view === 'month'"
        [attr.aria-pressed]="view === 'month'"
        (click)="setView('month')"
      >
        {{ 'SALES_INSIGHTS_CALENDAR_MONTH' | translate }}
      </button>
    </div>
  </div>

  <div class="si-calendar-layout" [class.si-calendar-layout--panel]="sidePanel">
    <div class="si-calendar-layout__main">
      <div
        class="si-calendar"
        [class.si-calendar--week]="view === 'week'"
        [class.si-calendar--month]="view === 'month'"
        [class.si-calendar--dragging]="!!dragId"
        [class.si-calendar--loading]="loading"
      >
        @if (view === 'month') {
          <div class="si-calendar__head si-calendar__head--week">{{ 'SALES_INSIGHTS_WEEK_SHORT' | translate }}</div>
          @for (weekday of weekdays; track $index) {
            <div class="si-calendar__head">{{ weekday }}</div>
          }
        }
        @for (week of weeks; track $index) {
          @if (view === 'month') {
            <div class="si-calendar__week">{{ week.number }}</div>
          }
          @for (day of week.days; track day.key) {
            <div
              class="si-calendar__day"
              role="button"
              tabindex="0"
              [class.si-calendar__day--outside]="day.outside"
              [class.si-calendar__day--weekend]="day.weekend"
              [class.si-calendar__day--today]="day.today"
              [class.si-calendar__day--past]="day.past"
              [class.si-calendar__day--selected]="sidePanel && selectedKey === day.key"
              [class.si-calendar__day--drop]="dropDay === day.key"
              [attr.aria-label]="day.label"
              [attr.aria-pressed]="sidePanel ? selectedKey === day.key : null"
              (click)="selectDay(day)"
              (keydown.enter)="selectDay(day)"
              (dragover)="dragOver(day, $event)"
              (dragleave)="dragLeave(day)"
              (drop)="drop(day, $event)"
            >
              <div class="si-calendar__day-head">
                @if (view === 'week') {
                  <span class="si-calendar__weekday">{{ day.weekday }}</span>
                }
                <span class="si-calendar__number">{{ day.number }}</span>
                @if (view === 'week' && day.items.length) {
                  <span class="si-calendar__day-count">{{ day.items.length }}</span>
                }
                @if (canBook && !day.past) {
                  <button
                    type="button"
                    class="si-calendar__add"
                    [disabled]="booking"
                    [attr.aria-label]="'SALES_INSIGHTS_NEW_ACTIVITY' | translate"
                    [title]="'SALES_INSIGHTS_NEW_ACTIVITY' | translate"
                    (click)="quickBook(day, $event)"
                    (keydown.enter)="$event.stopPropagation()"
                  >
                    <i class="fa-solid fa-plus"></i>
                  </button>
                }
              </div>
              <div class="si-calendar__events">
                @for (item of day.items | slice: 0 : maxPerDay; track item.row.id) {
                  <div
                    role="button"
                    tabindex="0"
                    class="si-calendar__event"
                    [class.si-calendar__event--done]="item.row.done"
                    [class.si-calendar__event--overdue]="item.overdue"
                    [class.si-calendar__event--saving]="saving.has(item.row.id)"
                    [class.si-calendar__event--draggable]="canDrag"
                    [style.--si-type-rgb]="item.typeRgb"
                    [title]="item.title"
                    [attr.draggable]="canDrag ? 'true' : null"
                    (dragstart)="dragStart(item, $event)"
                    (dragend)="dragEnd()"
                    (click)="open(item, $event)"
                    (keydown.enter)="open(item, $event)"
                  >
                    <span class="si-calendar__event-line">
                      <i
                        [class]="(item.row.done ? 'fa-solid fa-check' : item.typeIcon) + ' si-calendar__event-icon'"
                        aria-hidden="true"
                      ></i>
                      @if (item.time) {
                        <span class="si-calendar__event-time">{{ item.time }}</span>
                      }
                      @if (view === 'week') {
                        <span class="si-calendar__event-type">{{ item.typeName }}</span>
                      } @else {
                        <span class="si-calendar__event-name">{{ item.row.customerName || item.row.customerId }}</span>
                      }
                    </span>
                    @if (view === 'week') {
                      <span class="si-calendar__event-customer">{{
                        item.row.customerName || item.row.customerId
                      }}</span>
                      @if (item.row.city) {
                        <span class="si-calendar__event-city">{{ item.row.city }}</span>
                      }
                    }
                  </div>
                }
                @if (day.items.length > maxPerDay) {
                  <button type="button" class="si-calendar__more" (click)="showWeek(day, $event)">
                    {{ 'SALES_INSIGHTS_CALENDAR_MORE' | translate: { count: day.items.length - maxPerDay } }}
                  </button>
                }
              </div>
            </div>
          }
        }
      </div>

      @if (legend.length) {
        <div class="si-calendar__legend">
          @for (entry of legend; track entry.id) {
            <span class="si-calendar__legend-entry" [style.--si-type-rgb]="entry.rgb">
              <span class="si-calendar__legend-icon" aria-hidden="true"><i [class]="entry.icon"></i></span
              >{{ entry.name }}
            </span>
          }
        </div>
      }
    </div>

    @if (sidePanel) {
      <aside class="si-calendar-panel">
        <section class="si-calendar-panel__section">
          <header class="si-calendar-panel__head">
            <h5 class="si-calendar-panel__title">
              @if (selectedIsToday) {
                {{ 'SALES_INSIGHTS_CALENDAR_TODAY' | translate }}
              } @else {
                {{ selectedDay?.label }}
              }
            </h5>
            @if (selectedDay?.items?.length) {
              <span class="si-calendar-panel__count">{{ selectedDay?.items?.length }}</span>
            }
          </header>
          @if (selectedDay?.items?.length) {
            <ul class="si-agenda">
              @for (item of selectedDay?.items; track item.row.id) {
                <li>
                  <button
                    type="button"
                    class="si-agenda__item"
                    [class.si-agenda__item--done]="item.row.done"
                    [class.si-agenda__item--overdue]="item.overdue"
                    [style.--si-type-rgb]="item.typeRgb"
                    (click)="open(item, $event)"
                  >
                    <span class="si-agenda__time">{{ item.time }}</span>
                    <span class="si-agenda__icon" aria-hidden="true">
                      <i [class]="item.row.done ? 'fa-solid fa-check' : item.typeIcon"></i>
                    </span>
                    <span class="si-agenda__text">
                      <span class="si-agenda__customer">{{ item.row.customerName || item.row.customerId }}</span>
                      <span class="si-agenda__meta">
                        <span>{{ item.typeName }}</span>
                        @if (item.row.city) {
                          <span>{{ item.row.city }}</span>
                        }
                      </span>
                    </span>
                  </button>
                </li>
              }
            </ul>
          } @else if (!loading) {
            <div class="si-calendar-panel__empty">
              <span>{{ 'SALES_INSIGHTS_CALENDAR_NOTHING_PLANNED' | translate }}</span>
              @if (canBook && selectedDay && !selectedDay.past) {
                <button
                  type="button"
                  class="btn btn-sm si-calendar__button"
                  [disabled]="booking"
                  (click)="quickBook(selectedDay, $event)"
                >
                  <i class="fa-solid fa-plus"></i>{{ 'SALES_INSIGHTS_NEW_ACTIVITY' | translate }}
                </button>
              }
            </div>
          }
        </section>

        @if (visitLimit > 0) {
          <section class="si-calendar-panel__section">
            <header class="si-calendar-panel__head">
              <h5 class="si-calendar-panel__title">{{ 'SALES_INSIGHTS_VISIT_SUGGESTIONS' | translate }}</h5>
              @if (visits.length) {
                <span class="si-calendar-panel__count si-calendar-panel__count--warning">{{ visitTotal }}</span>
              }
            </header>
            @if (visitsLoading) {
              @for (row of [0, 1, 2]; track row) {
                <div class="si-visit si-visit--loading"><span class="si-calendar__bone"></span></div>
              }
            } @else if (visits.length) {
              <ul class="si-visits">
                @for (visit of visits; track visit.row.customerId) {
                  <li class="si-visit">
                    <span class="si-visit__icon" aria-hidden="true"><i class="fa-solid fa-store"></i></span>
                    <button type="button" class="si-visit__text" (click)="openCustomer(visit.row.customerId)">
                      <span class="si-visit__name">{{ visit.row.customerName || visit.row.customerId }}</span>
                      <span class="si-visit__meta">
                        @if (visit.row.city) {
                          <span>{{ visit.row.city }}</span>
                        }
                        <span>{{ visit.sinceKey | translate: visit.sinceParams }}</span>
                      </span>
                    </button>
                    @if (visit.booked) {
                      <span
                        class="si-visit__booked"
                        [title]="'SALES_INSIGHTS_VISIT_BOOKED' | translate: { date: visit.booked }"
                      >
                        <i class="fa-regular fa-calendar-check"></i>{{ visit.booked }}
                      </span>
                    } @else if (canBook) {
                      <lib-sales-insights-book-activity
                        class="si-visit__book"
                        [customerId]="visit.row.customerId"
                        [activityType]="visitType"
                        labelKey="SALES_INSIGHTS_BOOK_VISIT"
                        icon="fa-regular fa-calendar-plus"
                      ></lib-sales-insights-book-activity>
                    }
                  </li>
                }
              </ul>
            } @else {
              <div class="si-calendar-panel__empty">
                <i class="fa-solid fa-circle-check text-success"></i>
                <span>{{ 'SALES_INSIGHTS_VISIT_NONE' | translate }}</span>
              </div>
            }
          </section>
        }
      </aside>
    }
  </div>
</lib-sales-insights-card>
